import { fieldDef, optionLabel, stateLabel, type CrmEntity, type Lang } from "./fields";
import type { CrmRecord, CrmView, CrmViewCondition, CrmViewFilter, CrmViewSort } from "./api";
import { formatPhoneDisplay } from "@/lib/masks";

/** Lowercase without accents, for search and "contains" filters ("São" matches "sao"). */
export const normalize = (s: string | null | undefined): string =>
  (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export function formatDateTime(iso: string | null | undefined, lang: Lang, withTime = true): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const loc = lang === "pt" ? "pt-BR" : "en-US";
  return withTime
    ? d.toLocaleString(loc, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(loc, { day: "2-digit", month: "2-digit", year: "numeric" });
}

export const formatTime = (iso: string | null | undefined, lang: Lang): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(lang === "pt" ? "pt-BR" : "en-US", { hour: "2-digit", minute: "2-digit" });
};

/** Display text of one field of a record, as the grid and the audit history show it. */
export function displayValue(entity: CrmEntity, key: string, record: CrmRecord, lang: Lang): string {
  const raw = record.values[key];
  const def = fieldDef(entity, key);
  if (raw === null || raw === undefined || raw === "") return "";
  switch (def?.type) {
    case "datetime": return formatDateTime(String(raw), lang);
    case "option": return optionLabel(key, String(raw), lang);
    case "phone": return formatPhoneDisplay(String(raw));
    case "user": return record.names?.[key] || "";
    case "boolean":
      if (key === "isActive") return stateLabel(entity, !!raw, lang);
      return raw ? (lang === "pt" ? "Sim" : "Yes") : (lang === "pt" ? "Não" : "No");
    case "number":
    case "decimal": return typeof raw === "number" ? raw.toLocaleString(lang === "pt" ? "pt-BR" : "en-US") : String(raw);
    case "lookup": return record.names?.[key] || (lang === "pt" ? "Vinculado" : "Linked");
    default: return String(raw);
  }
}

/** Display text of a stored audit string (old/new value), with user ids resolved via members. */
export function displayAuditValue(entity: CrmEntity, key: string, value: string | null | undefined, lang: Lang, memberNames: Record<string, string>): string {
  if (value === null || value === undefined || value === "") return "";
  const def = fieldDef(entity, key);
  switch (def?.type) {
    case "datetime": return formatDateTime(value, lang);
    case "option": return optionLabel(key, value, lang);
    case "phone": return formatPhoneDisplay(value);
    case "user": return memberNames[value] || value;
    case "boolean":
      if (key === "isActive") return stateLabel(entity, value === "true", lang);
      return value === "true" ? (lang === "pt" ? "Sim" : "Yes") : (lang === "pt" ? "Não" : "No");
    default: return value;
  }
}

type SortValue = string | number | null;

function sortValue(entity: CrmEntity, key: string, record: CrmRecord, lang: Lang): SortValue {
  const raw = record.values[key];
  if (raw === null || raw === undefined || raw === "") return null;
  const type = fieldDef(entity, key)?.type;
  if (type === "datetime") {
    const t = Date.parse(String(raw));
    return Number.isNaN(t) ? null : t;
  }
  if (type === "number" || type === "decimal") return typeof raw === "number" ? raw : Number(raw);
  if (type === "boolean") return raw ? 1 : 0;
  return normalize(displayValue(entity, key, record, lang)) || null;
}

const collator = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

export function compareRecords(entity: CrmEntity, sort: CrmViewSort, lang: Lang) {
  return (a: CrmRecord, b: CrmRecord) => {
    const va = sortValue(entity, sort.key, a, lang);
    const vb = sortValue(entity, sort.key, b, lang);
    // Empty values always last, whatever the direction.
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : collator.compare(String(va), String(vb));
    return sort.dir === "desc" ? -cmp : cmp;
  };
}

function matchesCondition(entity: CrmEntity, c: CrmViewCondition, record: CrmRecord, lang: Lang): boolean {
  const def = fieldDef(entity, c.field);
  const raw = record.values[c.field];
  const empty = raw === null || raw === undefined || raw === "";
  if (c.op === "empty") return empty;
  if (c.op === "notempty") return !empty;

  const target = (c.value ?? "").trim();
  const type = def?.type;

  if (c.op === "gt" || c.op === "lt") {
    if (empty || !target) return false;
    let a: number, b: number;
    if (type === "datetime") {
      a = Date.parse(String(raw));
      b = Date.parse(target.length === 10 ? `${target}T00:00:00` : target);
      if (c.op === "gt" && target.length === 10) b += 86_400_000 - 1;   // "after 15/10" = after the whole day
    } else if (type === "number" || type === "decimal") {
      a = Number(raw);
      b = Number(target.replace(",", "."));
    } else {
      const cmp = collator.compare(displayValue(entity, c.field, record, lang), target);
      return c.op === "gt" ? cmp > 0 : cmp < 0;
    }
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    return c.op === "gt" ? a > b : a < b;
  }

  // eq / neq / contains / notcontains
  let actual: string;
  let wanted = normalize(target);
  if (type === "option" || type === "user" || type === "boolean") {
    actual = normalize(String(raw ?? ""));
  } else if (type === "datetime") {
    // Dates compare by day (yyyy-mm-dd) for eq/neq.
    const d = empty ? null : new Date(String(raw));
    actual = d && !Number.isNaN(d.getTime())
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      : "";
    wanted = target;
  } else if (type === "phone") {
    actual = (String(raw ?? "")).replace(/\D/g, "");
    wanted = target.replace(/\D/g, "") || wanted;
  } else {
    actual = normalize(displayValue(entity, c.field, record, lang));
  }
  switch (c.op) {
    case "eq": return actual === wanted;
    case "neq": return actual !== wanted;
    case "contains": return wanted.length === 0 || actual.includes(wanted);
    case "notcontains": return wanted.length === 0 || !actual.includes(wanted);
    default: return true;
  }
}

export function matchesFilter(entity: CrmEntity, filter: CrmViewFilter, record: CrmRecord, userId: string, lang: Lang): boolean {
  const active = record.values.isActive !== false;
  if (filter.state === "active" && !active) return false;
  if (filter.state === "inactive" && active) return false;
  if (filter.owner === "me") {
    const mine = record.values.ownerId === userId || (entity === "lead" && record.values.assignedToUserId === userId);
    if (!mine) return false;
  }
  return (filter.conditions || []).every((c) => matchesCondition(entity, c, record, lang));
}

/** Quick search: every word must appear in one of the visible columns, the name or the e-mail. */
export function matchesSearch(entity: CrmEntity, keys: string[], record: CrmRecord, query: string, lang: Lang): boolean {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = normalize(
    [...new Set([...keys, "fullName", "email", "personalEmail", "corporateEmail", "companyName", "topic"])]
      .map((k) => displayValue(entity, k, record, lang))
      .join(" "),
  );
  const digits = hay.replace(/\D/g, "");
  return words.every((w) => hay.includes(w) || (/^\d{3,}$/.test(w) && digits.includes(w)));
}

export function applyView(
  entity: CrmEntity,
  rows: CrmRecord[],
  view: Pick<CrmView, "columns" | "filter" | "sort">,
  opts: { userId: string; search: string; lang: Lang; sort?: CrmViewSort | null },
): CrmRecord[] {
  const keys = view.columns.map((c) => c.key);
  const sort = opts.sort || view.sort;
  const out = rows.filter(
    (r) => matchesFilter(entity, view.filter, r, opts.userId, opts.lang) && matchesSearch(entity, keys, r, opts.search, opts.lang),
  );
  if (sort?.key) out.sort(compareRecords(entity, sort, opts.lang));
  return out;
}

export const viewDisplayName = (v: Pick<CrmView, "name" | "namePt" | "nameEn">, lang: Lang): string =>
  (lang === "pt" ? v.namePt : v.nameEn) || v.name;

/** The record's title: full name for people, topic/company otherwise. */
export function recordTitle(entity: CrmEntity, values: Record<string, any>): string {
  const full = (values.fullName || `${values.firstName || ""} ${values.lastName || ""}`).trim();
  if (entity === "lead") return full || values.topic || values.companyName || "";
  return full || values.email || "";
}
